import * as d3 from "d3";

export function CustomBoxplot( data, {
  width = 300,                                          // Width of the graph (margins excluded)
  height = 200,                                         // Height of the graph (margins excluded)
  margin = {top:70, bottom:40, left:70, right:40},      // Empty space around the rendered graph (inside <svg>)
  legendRectWidth = 60,                                 // Legend rectangle width
  legendRectHeight = 20,                                // Legend rectangle height
  legendRectMarginRight = 15,                           // Legend rectangle margin right
  legendLabelMarginTop = 15,                            // Legend rectangle margin top
  labelTextSize = "15px",                               // Font size of axis and legend labels
  barColor = "#97cdf2",                                 // Color of the graph bars (turquoise default)
  barLineColor = "#43a8ec",                             // Color of the graph bars outline
  barLegend = "Target variable name",                   // Legend label
  xAxisLegend = "Filter variable name",                 // Prefix of labels on X axis
  errorLineColor = "red",                               // Color of the error lines (confidence intervals)
  errorLineWidthPercent = 50                            // Width of the error lines, in percents of the bar width
}) {
    // DEFINITION PART
    // --------------------

    // get a clone of input data and keys to access its data,
    // then map lower bound values and geometric means, so that no value is less than 0
    var dataValues = JSON.parse(JSON.stringify(data));
    const binKeys = Object.keys(dataValues);
    for (let i = 0; i < binKeys.length; i++) {
        dataValues[binKeys[i]].lowerBound = Math.max(0, dataValues[binKeys[i]].lowerBound);
        dataValues[binKeys[i]].geometricMean = Math.max(0, dataValues[binKeys[i]].geometricMean);
    }

    // get max value of Y domain
    var maxDomainY = 0;
    for (let i = 0; i < binKeys.length; i++) {
        maxDomainY = Math.max(maxDomainY, dataValues[binKeys[i]].upperBound);
    }

    // get X axis domain,
    // then get max length of X axis labels (label rotation + container size adjusted when too long)
    var domainX = binKeys.map(d => xAxisLegend + "-" + d);
    var maxLabelLengthX = 0;
    for (let i = 0; i < domainX.length; i++) {
        maxLabelLengthX = Math.max(maxLabelLengthX, getLabelWidth(domainX[i], labelTextSize));
    }

    // create definition of Y axis properties
    var yScale = d3.scaleLinear()
       .domain([0, maxDomainY])                   // max of domain is the greatest upperbound
       .nice()                                    // round domain so that generated Y axis always includes max domain value
       .range([height, 0]);                       // length of rendered axis (d3.axisLeft() draws from above to bottom)

    // create definition of X axis properties
    var xScale = d3.scaleBand()
       .domain(domainX)                           // domain categories to be placed on X axis (axis labels, as well as callable keys to get coordinates from xScale)
       .range([0, width])                         // interval on which categories are mapped (length of X axis in window)
       .padding(0.2);

    // if X labels bigger then bar width, adjust container size to make space for rotated label
    // label gets rotated by 45 degrees, so use pythagoras theorem to get necessary offset
    if (maxLabelLengthX > xScale.bandwidth()){
        margin.bottom += Math.sqrt((maxLabelLengthX * maxLabelLengthX) /2);
    }

    // create drawing area, big enough to include graph margins
    const svg = d3.create("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);

    // create group to contain all elements of the graph (easier positioning)
    const mainGroup = svg.append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // create legend rectangle and label nodes
    const legendRect = d3.create("rect")
       .attr("x", 0)
       .attr("y", 0)
       .attr("width", legendRectWidth)
       .attr("height", legendRectHeight)
       .style("fill", barColor)
       .attr("opacity", "80%")
       .style("stroke", barLineColor)
       .attr('stroke-width', 2);
    const legendLabel = d3.create("text")
       .attr("transform", "translate("+(legendRectWidth + legendRectMarginRight)+", "+ legendLabelMarginTop +")")
       .attr("font-size", labelTextSize)
       .text(barLegend);
    const totalLegendWidth = legendRectWidth + legendRectMarginRight + getLabelWidth(barLegend, labelTextSize);

    // RENDERING PART
    // --------------------

    // render grid lines
    mainGroup.selectAll("line.horizontalGrid")
        .data(yScale.ticks())
        .enter()
        .append("line")
            .attr("class", "horizontalGrid")
            .attr("x1", 0)
            .attr("x2", width)
            .attr("y1", function(d){ return yScale(d);})
            .attr("y2", function(d){ return yScale(d);})
            .attr("stroke", "black")
            .attr("stroke-opacity", "20%");

    // render bars
    mainGroup.selectAll("rect")
        .data(binKeys)
        .enter()
        .append("rect")      // rendered bar definition (created whenever there is more data than rendered selection elements)
           .attr("transform", function(d) { return "translate(" + xScale(xAxisLegend + "-" + d) + "," + yScale( dataValues[d].geometricMean ) +  ")"; })
           .attr("width", xScale.bandwidth())
           .attr("height", function(d) { return height - yScale( dataValues[d].geometricMean ); })
           .style("fill", barColor)
           .attr("opacity", "80%")
           .style("stroke", barLineColor)
           .attr('stroke-width', 2);

    // render error lines (confidence intervals)
    mainGroup.selectAll("line.errorLine")
        .data(binKeys)
        .join( (enter) => {
            let enterGroup = enter.append("g");

            enterGroup.append("line")
                .attr("transform", function(d) { return "translate(" + (xScale(xAxisLegend + "-" + d) + xScale.bandwidth()/2) + ",0)"; })
                .attr("class", "errorLine")
                .attr("x1",  - (xScale.bandwidth()/200)* errorLineWidthPercent )
                .attr("x2",  + (xScale.bandwidth()/200)* errorLineWidthPercent )
                .attr("y1", function(d) { return yScale(dataValues[d].upperBound);})
                .attr("y2", function(d) { return yScale(dataValues[d].upperBound);})
                .attr("stroke", errorLineColor)
                .attr("stroke-opacity", "80%");

            enterGroup.append("line")
                .attr("transform", function(d) { return "translate(" + (xScale(xAxisLegend + "-" + d) + xScale.bandwidth()/2) + ",0)"; })
                .attr("class", "errorLine")
                .attr("x1",  - (xScale.bandwidth()/200)* errorLineWidthPercent )
                .attr("x2",  + (xScale.bandwidth()/200)* errorLineWidthPercent )
                .attr("y1", function(d) { return yScale(dataValues[d].lowerBound);})
                .attr("y2", function(d) { return yScale(dataValues[d].lowerBound);})
                .attr("stroke", errorLineColor)
                .attr("stroke-opacity", "80%");

            enterGroup.append("line")
                .attr("transform", function(d) { return "translate(" + (xScale(xAxisLegend + "-" + d) + xScale.bandwidth()/2) + ",0)"; })
                .attr("class", "errorLine")
                .attr("x1", 0)
                .attr("x2", 0)
                .attr("y1", function(d) { return yScale(dataValues[d].upperBound);})
                .attr("y2", function(d) { return yScale(dataValues[d].lowerBound);})
                .attr("stroke", errorLineColor)
                .attr("stroke-opacity", "80%");
        });

    // render X axis (overwrite bottom outline of bars)
    const xlabelGroup = mainGroup.append("g")                    // add new group of elements to main group
         .attr("transform", "translate(0," + height + ")")       // positioning of elements in the group
         .call(d3.axisBottom(xScale));                            // in current selection, call function that renders the X axis

    // adjust X labels font size
    // also if X labels too long, rotate them by 45 degrees
    xlabelGroup.selectAll("text").attr("font-size", labelTextSize);
    if (maxLabelLengthX > xScale.bandwidth()){
        xlabelGroup.selectAll("text")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)");
    }

    // render Y axis to graph
    mainGroup.append("g")
       .call(d3.axisLeft(yScale));

    // render Y axis label
    svg.append("text")
        .attr("transform", "rotate(-90 "+(margin.left/2)+" "+(height/2 + margin.top)+"), translate(-20," + (height/2 + margin.top) + ")")
        .attr("dy", "-.3em")
        .attr("font-size", labelTextSize)
        .text("Geometric mean");

    // render bar legend (rectangle and label)
    svg.append("g")
       .attr("transform", "translate("+ ((margin.left + width + margin.right)/2 - totalLegendWidth/2) +"," + (margin.top/2 - legendRectHeight/2) + ")")
       .append(function() {return legendRect.node()});
    svg.append("g")
       .attr("transform", "translate("+ ((margin.left + width + margin.right)/2 - totalLegendWidth/2) +"," + (margin.top/2 - legendRectHeight/2) + ")")
       .append(function() {return legendLabel.node()});

    return svg.node();
}

// helper function, to get label text width
function getLabelWidth(text, fontSize) {
    var dummySvg = d3.select("body")
        .append("svg");
    dummySvg.append("text")
        .attr("x", -100000)
        .attr("y", -100000)
        .attr("font-size", fontSize)
        .text(text);
    const size = dummySvg.node().getBBox();
    dummySvg.remove();
    return size.width;
}
